import { Badge, Box, Flex, IconButton, Text } from '@radix-ui/themes';
import { RotateCw } from 'lucide-react';
import type { CodexUsage } from '@mac-remote/protocol';
import { shownLimits, untilReset, usedColor, type ShownLimit } from '../lib/codexLimits';

/**
 * A bar that fills as the window is spent — the same direction as the number beside
 * it, which counts up. One quantity, shown twice, moving the same way.
 */
export function Bar({ used, color }: { used: number; color: 'jade' | 'amber' | 'red' }) {
  return (
    <Box style={{ height: 5, borderRadius: 999, background: 'var(--gray-a5)', overflow: 'hidden' }}>
      <Box
        style={{
          height: '100%',
          width: `${Math.max(0, Math.min(100, used))}%`,
          background: `var(--${color}-9)`,
          transition: 'width 400ms ease, background 400ms ease',
        }}
      />
    </Box>
  );
}

function LimitRow({ limit, now }: { limit: ShownLimit; now: number }) {
  const color = usedColor(limit.used);
  const reset = untilReset(limit.resetsAt, now);

  return (
    <Box style={{ padding: '10px 12px' }}>
      <Flex align="baseline" justify="between" gap="3">
        <Text size="2" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {limit.title}
        </Text>
        <Text size="3" weight="bold" style={{ flex: '0 0 auto', color: `var(--${color}-11)` }}>
          {Math.round(limit.used)}%
        </Text>
      </Flex>
      <Flex align="baseline" justify="between" gap="3" style={{ margin: '1px 0 7px' }}>
        <Text size="1" style={{ color: 'var(--app-hint)' }}>
          {reset ? `сброс ${reset}` : 'сброс не объявлен'}
        </Text>
        <Text size="1" style={{ flex: '0 0 auto', color: 'var(--app-hint)' }}>
          использовано
        </Text>
      </Flex>
      <Bar used={limit.used} color={color} />
    </Box>
  );
}

/** Read-only subscription data supplied by Codex app-server on the Mac. */
export function CodexLimits({ usage, onRefresh }: { usage: CodexUsage | null; onRefresh: () => void }) {
  const now = Date.now();
  const limits = usage?.ok ? shownLimits(usage) : [];

  return (
    <Box>
      <Flex align="center" justify="between" gap="2" style={{ padding: '0 12px' }}>
        <Text size="1" weight="bold" style={{ color: 'var(--app-hint)', letterSpacing: 0.4 }}>
          ЛИМИТЫ CODEX
        </Text>
        <Flex align="center" gap="2">
          {usage?.ok && usage.plan && (
            <Badge size="1" variant="soft" color="gray">
              {usage.plan}
            </Badge>
          )}
          <IconButton size="1" variant="ghost" color="gray" onClick={onRefresh} title="Обновить">
            <RotateCw size={14} />
          </IconButton>
        </Flex>
      </Flex>

      <Box
        style={{ marginTop: 6, borderRadius: 12, background: 'var(--app-secondary-bg)', overflow: 'hidden' }}
      >
        {!usage ? (
          <Box style={{ padding: 12 }}>
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              Мак не на связи — лимиты читает Codex на нём.
            </Text>
          </Box>
        ) : !usage.ok ? (
          <Box style={{ padding: 12 }}>
            <Text size="2" style={{ color: 'var(--amber-11)' }}>
              {usage.error}
            </Text>
          </Box>
        ) : limits.length === 0 ? (
          <Box style={{ padding: 12 }}>
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              Codex не вернул подписочные окна для этого аккаунта.
            </Text>
          </Box>
        ) : (
          limits.map((limit) => <LimitRow key={limit.key} limit={limit} now={now} />)
        )}

        {usage?.ok && usage.credits && (
          <Flex
            align="center"
            justify="between"
            gap="3"
            style={{ padding: '10px 12px', borderTop: '1px solid var(--gray-a4)' }}
          >
            <Box style={{ minWidth: 0 }}>
              <Text size="2">Кредиты сверх лимита</Text>
              <Text size="1" as="p" style={{ color: 'var(--app-hint)', margin: '2px 0 0' }}>
                баланс, сообщённый Codex
              </Text>
            </Box>
            <Text size="2" weight="medium" style={{ flex: '0 0 auto' }}>
              {usage.credits.unlimited ? 'Без ограничений' : usage.credits.balance ??
                (usage.credits.hasCredits ? 'Доступны' : 'Нет кредитов')}
            </Text>
          </Flex>
        )}
      </Box>

      {usage && (
        <Text size="1" as="p" style={{ color: 'var(--app-hint)', margin: '6px 12px 0' }}>
          {usage.ok ? 'прочитано' : 'попытка'} в {new Date(usage.at).toLocaleTimeString('ru-RU')}
        </Text>
      )}
    </Box>
  );
}
